/* Gallery slider — Odd Ritual's "is-large" home gallery, dependency-free.
   Same clip-path wipe + image parallax as the hero slider, plus a counter.
   Interruptible: a new prev/next cancels the running wipe and starts the
   next one immediately, so the buttons can be spammed like the reference.
   Registry-managed: re-inits after every Barba swap; all listeners carry the
   page's abort signal so re-running never double-binds. */
(function () {
  var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var WIPE_MS = 1000;

  var FULL = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
  var TOP_EDGE = 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)';
  var BOTTOM_EDGE = 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)';

  function init(ctx) {
  var signal = ctx && ctx.signal;

  document.querySelectorAll('[data-gallery]').forEach(function (root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    var counter = root.querySelector('[data-gallery-count]');
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var activeIndex = 0;
    var running = [];

    if (slides.length < 2) return;

    function cancelRunning() {
      running.forEach(function (a) { a.cancel(); });
      running = [];
      slides.forEach(function (slide) { slide.style.zIndex = ''; });
    }

    var pendingPhoto = 0;
    function requestSlide(nextIndex, forwards) {
      var request = ++pendingPhoto;
      var img = slides[nextIndex].querySelector('img');
      // Editorial photos load eagerly. On a slow connection retain the current
      // photo until the requested one can be painted, rather than exposing colour.
      if (root.classList.contains('gallery--editorial') && img && img.decode) {
        img.decode().then(function () {
          if (request === pendingPhoto && !(signal && signal.aborted)) moveSlide(nextIndex, forwards);
        }).catch(function () { /* retain the current photo if delivery fails */ });
      } else {
        moveSlide(nextIndex, forwards);
      }
    }

    function moveSlide(nextIndex, forwards) {
      if (nextIndex === activeIndex) return;

      // interrupt any wipe in progress: snap it to its end state (classes
      // below define the resting look) and start the new one right away
      cancelRunning();

      var prev = slides[activeIndex];
      var next = slides[nextIndex];
      var prevImg = prev.querySelector('[data-gallery-img]');
      var nextImg = next.querySelector('[data-gallery-img]');

      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === nextIndex);
        slide.setAttribute('aria-hidden', i === nextIndex ? 'false' : 'true');
      });
      activeIndex = nextIndex;
      if (counter) counter.textContent = String(nextIndex + 1);

      if (reduceMotion) return;

      prev.style.zIndex = '1';
      next.style.zIndex = '2';

      var opts = { duration: WIPE_MS, easing: EASE };
      var anims = [];

      if (forwards) {
        anims.push(next.animate([{ clipPath: TOP_EDGE }, { clipPath: FULL }], opts));
        anims.push(prev.animate([{ clipPath: FULL }, { clipPath: BOTTOM_EDGE }], opts));
        anims.push(prevImg.animate([{ transform: 'translateY(0%)' }, { transform: 'translateY(10%)' }], opts));
        anims.push(nextImg.animate([{ transform: 'translateY(-10%)' }, { transform: 'translateY(0%)' }], opts));
      } else {
        anims.push(next.animate([{ clipPath: BOTTOM_EDGE }, { clipPath: FULL }], opts));
        anims.push(prev.animate([{ clipPath: FULL }, { clipPath: TOP_EDGE }], opts));
        anims.push(prevImg.animate([{ transform: 'translateY(0%)' }, { transform: 'translateY(-10%)' }], opts));
        anims.push(nextImg.animate([{ transform: 'translateY(10%)' }, { transform: 'translateY(0%)' }], opts));
      }

      running = anims;

      Promise.all(
        anims.map(function (a) { return a.finished.catch(function () {}); })
      ).then(function () {
        // only clean up if we're still the latest batch (not interrupted)
        if (running === anims) {
          anims.forEach(function (a) { a.cancel(); });
          running = [];
          prev.style.zIndex = '';
          next.style.zIndex = '';
        }
      });
    }

    function goNext() {
      requestSlide((activeIndex + 1) % slides.length, true);
    }

    function goPrev() {
      requestSlide((activeIndex - 1 + slides.length) % slides.length, false);
    }

    if (nextBtn) nextBtn.addEventListener('click', goNext, { signal: signal });
    if (prevBtn) prevBtn.addEventListener('click', goPrev, { signal: signal });

    /* --- floating "( Prev )" / "( Next )" label tracing the cursor ---
       Tracked on the section itself (not per zone), so crossing the middle
       has no event seam: the side simply flips by cursor position and the
       label glides over — no snap, no blink. The zones stay click targets. */
    var cursorEl = root.querySelector('[data-gallery-cursor]');
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (cursorEl) {
      var cx = 0, cy = 0, tx = 0, ty = 0;
      var cursorRaf = null;
      var cursorActive = false;
      // trailing glide — snappier, closer to the reference's power3 quickTo
      var CURSOR_LERP = 0.15;

      var applyCursor = function () {
        cursorEl.style.transform = 'translate(' + cx.toFixed(1) + 'px, ' + cy.toFixed(1) + 'px)';
      };

      var cursorTick = function () {
        cx += (tx - cx) * CURSOR_LERP;
        cy += (ty - cy) * CURSOR_LERP;
        applyCursor();
        if (cursorActive || Math.abs(tx - cx) > 0.5 || Math.abs(ty - cy) > 0.5) {
          cursorRaf = requestAnimationFrame(cursorTick);
        } else {
          cursorRaf = null;
        }
      };

      var currentSide = null;

      var setTarget = function (e) {
        var rect = root.getBoundingClientRect();
        var isNext = e.clientX - rect.left > rect.width / 2;
        if (isNext !== currentSide) {
          currentSide = isNext;
          cursorEl.textContent = isNext ? '( Next )' : '( Prev )';
        }
        // viewport coordinates (the label is position:fixed, so it stays glued
        // to the cursor's height while the page scrolls — no side flip at the
        // middle), clamped so it never crosses the section's right edge
        var x = e.clientX + 18;
        x = Math.min(x, rect.right - cursorEl.offsetWidth - 18);
        tx = x;
        ty = e.clientY - cursorEl.offsetHeight / 2;
      };

      // last cursor position (viewport), so a scroll can tell if the cursor is
      // still over the section without a pointer event firing
      var lastClientX = 0, lastClientY = 0;

      var activate = function (e) {
        setTarget(e);
        // start at the cursor, no fly-in
        cx = tx;
        cy = ty;
        applyCursor();
        cursorEl.classList.add('is-visible');
        cursorActive = true;
        if (!cursorRaf && !reduceMotion) cursorRaf = requestAnimationFrame(cursorTick);
      };

      root.addEventListener('pointerenter', function (e) {
        if (!finePointer.matches || e.pointerType === 'touch') return;
        lastClientX = e.clientX;
        lastClientY = e.clientY;
        activate(e);
      }, { signal: signal });

      root.addEventListener('pointermove', function (e) {
        if (!finePointer.matches || e.pointerType === 'touch') return;
        lastClientX = e.clientX;
        lastClientY = e.clientY;
        // re-arm if a scroll had faded it out while the cursor stayed put
        if (!cursorActive) { activate(e); return; }
        setTarget(e);
        if (reduceMotion) {
          cx = tx;
          cy = ty;
          applyCursor();
        }
      }, { signal: signal });

      root.addEventListener('pointerleave', function () {
        cursorActive = false;
        cursorEl.classList.remove('is-visible');
      }, { signal: signal });

      // the label is fixed to the viewport, so scrolling the gallery out from
      // under a still cursor never fires pointerleave — fade it out once the
      // cursor's point is no longer over the section. Only toggles a class
      // (no transform write), so it doesn't reintroduce scroll flicker.
      window.addEventListener('scroll', function () {
        if (!cursorActive) return;
        var rect = root.getBoundingClientRect();
        var inside =
          lastClientX >= rect.left && lastClientX <= rect.right &&
          lastClientY >= rect.top && lastClientY <= rect.bottom;
        if (!inside) {
          cursorActive = false;
          cursorEl.classList.remove('is-visible');
        }
      }, { passive: true, signal: signal });

      // stop the label's rAF glide when this page is swapped away
      if (signal) {
        signal.addEventListener('abort', function () {
          if (cursorRaf) cancelAnimationFrame(cursorRaf);
          cursorRaf = null;
          cursorActive = false;
        });
      }
    }

    /* --- swipe (touch) --- */
    var startX = null;
    root.addEventListener('pointerdown', function (e) {
      startX = e.clientX;
    }, { signal: signal });
    root.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      startX = null;
      if (Math.abs(dx) < 60) return;
      if (dx < 0) {
        goNext();
      } else {
        goPrev();
      }
    }, { signal: signal });
  });
  }

  if (window.Onni && window.Onni.register) {
    window.Onni.register('gallery-slider', init);
  } else {
    init();
  }
})();
